'use client'

import React, { useContext, useMemo } from 'react'
import { usePathname } from 'next/navigation'
import { ChevronDown, Search, X } from 'lucide-react'

import { useSession, useTenant } from '@rewardops/auth'
import {
  cn,
  LocalizedLink,
  useFavouritingAndWishlistingStore,
  useFilter,
  useLocale,
} from '@rewardops/ui'
import { isValidUrl } from '@rewardops/utils'

import { filterNavLinks } from '~/utils/filter-utils'
import { formatTitleToId } from '~/utils/id-format-utils'
import { getSearchDropdownOptions, SearchTypes } from '~/utils/sort-utils'
import { useErrorToast } from '~/components/error-toast'
import { useIsClient } from '~/components/hooks/use-is-client'
import { useLocaleChange } from '~/components/hooks/use-locale-change'
import { useNavigationLinks } from '~/components/hooks/use-navigation-links'
import { useSearchBox } from '~/components/hooks/use-search-box'
import { MapleSelect } from '~/components/maple/components/select'
import MapleLogo from '~/components/maple/maple-logo'
import { CategoryGrid } from '~/components/maple/navbar/category-grid'
import {
  HeartLink,
  ShoppingCartLink,
} from '~/components/maple/navbar/link-icons'
import { PointsBalance } from '~/components/maple/navbar/points-balance'
import { styling } from '~/components/styling'
import { useTranslation } from '~/app/i18n/client'
import { useCart } from '~/context/cart-context'
import { ElpPromotionsContext } from '~/context/elp-promotions-context'
import { PromotionsContext } from '~/context/promotions-context'
import { ResourcesContext } from '~/context/resources-provider'
import { DesktopMenuLink } from './desktop-menu-link'
import { ProfileMenu } from './profile-dropdown-menu'
import { SearchBar } from './search-bar'
import { SearchOverlay } from './search-overlay'

export function DesktopStackedNavBar({
  categories,
  isLoading,
}: {
  categories: any
  isLoading: boolean
}) {
  const { t } = useTranslation()
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const { data: session } = useSession()
  const tenant = useTenant()
  const { getCartQuantity } = useCart()
  const { locale } = useLocale()
  const pathname = usePathname()
  const { triggerErrorToast } = useErrorToast()
  const resources = useContext(ResourcesContext)

  const { searchQuery, searchType } = useFilter()

  const { promotionsLoading, totalItems } = useContext(PromotionsContext)
  const { showPromotionsLink } = useContext(ElpPromotionsContext)

  const {
    isSearchBarOpened,
    openSearchBar,
    debouncedState,
    updateSearchQuery,
    clearSearchInput,
    resetSearch,
  } = useSearchBox()

  const searchOverlay = useMemo(() => {
    if (!searchQuery || !searchType) return null
    return <SearchOverlay />
  }, [searchQuery, searchType])

  const { savedItemIds } = useFavouritingAndWishlistingStore((state) => state)

  const cartQuantity = getCartQuantity()

  const fontClass =
    tenant.styleVariant?.header?.links?.weight || 'font-semibold'

  const handleTravelClick = (e: React.MouseEvent, link: any) => {
    if (
      link?.url?.includes('/travel') &&
      tenant?.favicon === 'mbna' &&
      !session?.user?.member?.id
    ) {
      e.preventDefault()
      triggerErrorToast({
        exclamation: t('travelLoginErrorToast.exclamation'),
        message: t('travelLoginErrorToast.message'),
      })
      return
    }
  }

  const handleMouseEnter = () => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
    }
    setIsDropdownOpen(true)
  }

  // Add some debounce to the dropdown close
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false)
    }, 250)
  }

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault()
      setIsDropdownOpen(true)
    }
  }

  const howItWorksPath = `/${locale}/how-it-works`
  const lastSegment = pathname?.split('/').filter(Boolean).pop()
  const isNotHowItWorks = pathname !== howItWorksPath

  const showSpecialOffers =
    !promotionsLoading && totalItems > 0 && tenant?.flags.specialOffersPage

  const { directLinks, menuLinks } = useNavigationLinks({
    direct: resources?.navigationMenu?.directLinks,
    menu: resources?.navigationMenu?.menuLinks,
  })

  return (
    <>
      <div className="border-b bg-tenant-primary-color">
        <div className="container flex flex-row items-center justify-between gap-6 p-5 lg:max-w-[1250px]">
          <MapleLogo />
          {isSearchBarOpened ? (
            <>
              <div className="relative w-1/2">
                <SearchBar
                  selector={
                    <MapleSelect
                      id={`search-type-selector`}
                      className="w-auto rounded-r-md border bg-gray-100 px-1 pl-2 pr-1 text-sm"
                      selectItemClassName="font-normal text-grey-300"
                      placeholder="Select search type"
                      defaultValue={searchType || SearchTypes.Products}
                      options={getSearchDropdownOptions(t)}
                      onValueChange={(option) =>
                        updateSearchQuery({
                          query: searchQuery,
                          qType: option,
                        })
                      }
                      withBorder={true}
                    />
                  }
                  onChange={updateSearchQuery}
                  onClose={() => clearSearchInput()}
                  searchQuery={debouncedState.query}
                  selectorDisabled={!tenant?.flags?.retailers}
                />
              </div>
              <button
                onClick={() => resetSearch()}
                className="opacity-70 transition-opacity hover:opacity-100"
              >
                <X className="h-6 w-6 text-white hover:text-white" />
              </button>
            </>
          ) : (
            <>
              {tenant?.stackedNavBarLinks?.map((link, i) =>
                isValidUrl(link?.url) ? (
                  <a
                    key={`${formatTitleToId(link?.label[locale] || '')}-${i}`}
                    href={link?.url}
                    id={formatTitleToId(link?.label[locale] || '')}
                    target="_blank"
                    className={cn(
                      `inline-flex gap-1 text-sm`,
                      fontClass,
                      styling.LinkTwo,
                    )}
                  >
                    {link?.label[locale]}
                  </a>
                ) : (
                  <LocalizedLink
                    key={`${formatTitleToId(link?.label[locale] || '')}-${i}`}
                    href={link?.url}
                    id={formatTitleToId(link?.label[locale] || '')}
                    className={cn(
                      `inline-flex gap-1 text-sm`,
                      fontClass,
                      styling.LinkTwo,
                      lastSegment ===
                        formatTitleToId(link?.label[locale] || '') &&
                        i === 2 &&
                        'border-b-2 border-white',
                      isNotHowItWorks && i === 0 && 'border-b-2 border-white',
                    )}
                    onClick={(e: any) => handleTravelClick(e, link)}
                  >
                    {link?.label[locale]}
                  </LocalizedLink>
                ),
              )}

              {/* contentful navigation menu */}
              {filterNavLinks(
                directLinks,
                tenant?.theme,
                showPromotionsLink,
              )?.map((directLink) => (
                <LocalizedLink
                  key={directLink?.id}
                  href={directLink?.to}
                  id={directLink?.id}
                  className={cn('inline-flex gap-1 text-sm', styling.Link)}
                >
                  {directLink?.name}
                </LocalizedLink>
              ))}

              {menuLinks?.map((menu) => (
                <DesktopMenuLink key={menu?.id} menu={menu as any} />
              ))}

              <div className="ml-auto flex items-center gap-6">
                {session?.user?.member ? (
                  <PointsBalance member={session?.user?.member} color="white" />
                ) : (
                  <ProfileMenu color="white" />
                )}
                <button
                  id="search"
                  className="hover:text-tenant-primary-color"
                  aria-label={t('nav.search')}
                  onClick={() => openSearchBar()}
                >
                  <Search className="text-white" />
                </button>
                <div>
                  <LanguageSwitcher color="white" hoverEffect={false} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {isNotHowItWorks && (
        <div className="border-b bg-white">
          <div className="container flex flex-row items-center justify-between gap-6 p-5 lg:max-w-[1250px]">
            <div
              id="nav-bar-category-grid"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                id="nav-bar-shop-categories-button"
                className={cn(
                  `inline-flex gap-1 text-sm`,
                  isDropdownOpen && 'text-tenant-primary-color',
                  fontClass,
                  styling.Link,
                )}
                onKeyDown={handleKeyDown}
                aria-haspopup="true"
                aria-expanded={isDropdownOpen}
              >
                {t('nav.shopCategories')}
                <ChevronDown className="h-6 w-6" />
              </button>
              {isDropdownOpen && !isLoading && (
                <div className="absolute left-0 right-0 top-full z-10 w-screen bg-white shadow-md">
                  <div className="custom-scrollbar max-h-[70vh] overflow-y-auto">
                    <div className="container mx-auto lg:max-w-[1250px]">
                      <LocalizedLink
                        id={`main-category-view-all`}
                        href={`/category/all`}
                        onClick={() => setIsDropdownOpen(false)}
                        className={cn(
                          'ml-4 mr-12 mt-4 inline-block text-xl font-bold uppercase text-gray-800',
                          styling.Link,
                        )}
                      >
                        {t('general.viewAll')}
                      </LocalizedLink>
                      <CategoryGrid
                        categories={categories}
                        setIsDropdownOpen={setIsDropdownOpen}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            {showSpecialOffers && (
              <LocalizedLink
                href="/special-offers"
                id="deals-link"
                className={cn('inline-flex gap-1 text-sm', styling.Link)}
              >
                {t('nav.specialOffers')}
              </LocalizedLink>
            )}
            {tenant?.flags?.retailers && (
              <LocalizedLink
                href="/retailers?sort=name_ascending"
                id="shop-and-earn-link"
                className={cn('inline-flex gap-1 text-sm', styling.Link)}
              >
                {t('nav.shopAndEarn')}
              </LocalizedLink>
            )}
            {tenant?.flags?.payAnywhere && (
              <LocalizedLink
                href="/pay-anywhere"
                id="pay-anywhere-link"
                className={cn('inline-flex gap-1 text-sm', styling.Link)}
              >
                Pay Anywhere
              </LocalizedLink>
            )}
            <div className="ml-auto flex items-center gap-6">
              {session?.user && (
                <HeartLink savedItemsCount={savedItemIds?.size ?? 0} />
              )}
              <ShoppingCartLink cartQuantity={cartQuantity} />
            </div>
          </div>
        </div>
      )}
      {/* Full-page search overlay */}
      {searchOverlay}
    </>
  )
}

const LanguageSwitcher = ({
  color = 'black',
  hoverEffect,
}: {
  color?: string
  hoverEffect?: boolean
}) => {
  const isClient = useIsClient()
  const tenant = useTenant()
  const { currentLanguage, availableLanguages, changeLocale } =
    useLocaleChange()

  // skip rendering on the server to avoid hydration issues
  // since the locale is not available at that time
  if (!isClient) return null

  const defaultOption = availableLanguages.find(
    (lang) => lang.key === currentLanguage,
  )

  const fontClass =
    tenant.styleVariant?.header?.links?.weight || 'font-semibold'

  return (
    <MapleSelect
      id="language-selector"
      className={cn(
        `text-sm text-${color} ${
          hoverEffect ? 'hover:text-tenant-primary-color' : ''
        }`,
        fontClass,
      )}
      placeholder={defaultOption?.value || ''}
      defaultValue={defaultOption?.key}
      options={availableLanguages}
      onValueChange={(lang) => changeLocale(lang)}
    />
  )
}
